import { useEffect, useState, type FormEvent } from 'react';
import { supabase } from '@/lib/supabase';

interface SupportRequest {
  id: string;
  request_code: string;
  name: string;
  subject: string;
  message: string;
  status: string;
  created_at: string;
  reply_count: number;
  last_reply_at: string | null;
}

interface Reply {
  content: string;
  is_staff: boolean;
  created_at: string;
}

const statusMeta: Record<string, { label: string; cls: string }> = {
  open: { label: 'Mới', cls: 'bg-accent-500/15 text-accent-400' },
  answered: { label: 'Đã trả lời', cls: 'bg-emerald-500/15 text-emerald-400' },
  resolved: { label: 'Đã giải quyết', cls: 'bg-secondary-500/15 text-secondary-400' },
};

const formatDate = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export default function Mailbox({ onLogout }: { onLogout: () => void }) {
  const [requests, setRequests] = useState<SupportRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [replies, setReplies] = useState<Reply[]>([]);
  const [replyLoading, setReplyLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: rpcError } = await supabase.rpc('list_support_requests');
      if (rpcError) throw rpcError;
      setRequests((data as SupportRequest[]) || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể tải danh sách yêu cầu.');
    } finally {
      setLoading(false);
    }
  };

  const selected = requests.find((r) => r.id === selectedId) || null;

  const loadReplies = async (req: SupportRequest) => {
    setReplyLoading(true);
    try {
      const { data, error: rpcError } = await supabase.rpc('get_support_request', { p_code: req.request_code });
      if (rpcError) throw rpcError;
      const payload = data as { replies?: Reply[] } | null;
      setReplies((payload?.replies as Reply[]) || []);
    } catch {
      setReplies([]);
    } finally {
      setReplyLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selected) {
      loadReplies(selected);
    } else {
      setReplies([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const handleReply = async (e: FormEvent) => {
    e.preventDefault();
    if (!selected || !replyText.trim()) return;
    setSending(true);
    try {
      const { error: rpcError } = await supabase.rpc('reply_support_request', {
        p_request_id: selected.id,
        p_content: replyText.trim(),
      });
      if (rpcError) throw rpcError;
      setReplyText('');
      await loadReplies(selected);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể gửi trả lời.');
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!selected) return;
    try {
      const { error: rpcError } = await supabase.rpc('update_support_request_status', {
        p_request_id: selected.id,
        p_status: newStatus,
      });
      if (rpcError) throw rpcError;
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể cập nhật trạng thái.');
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl font-bold text-foreground-950">Hộp thư hỗ trợ</h1>
          <p className="text-foreground-600 text-xs mt-0.5">Tổng {requests.length} yêu cầu</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="px-3 py-1.5 rounded-md border border-background-300/60 text-xs font-semibold text-foreground-600 hover:text-primary-400 whitespace-nowrap cursor-pointer transition-colors"
          >
            <i className="ri-refresh-line mr-1"></i> Tải lại
          </button>
          <button
            onClick={onLogout}
            className="px-3 py-1.5 rounded-md border border-background-300/60 text-xs font-semibold text-foreground-600 hover:text-rose-400 whitespace-nowrap cursor-pointer transition-colors"
          >
            <i className="ri-logout-box-r-line mr-1"></i> Đăng xuất
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-rose-500/15 border border-rose-500/30 p-3 flex items-center gap-2">
          <i className="ri-error-warning-line text-rose-400 text-lg"></i>
          <p className="text-sm text-rose-400">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="py-16 text-center text-sm text-foreground-600">Đang tải...</div>
      ) : requests.length === 0 ? (
        <div className="rounded-xl border border-background-200/60 bg-background-100 p-10 text-center">
          <i className="ri-inbox-line text-3xl text-foreground-500"></i>
          <p className="mt-2 text-foreground-950 font-semibold text-sm">Chưa có yêu cầu nào</p>
          <p className="mt-1 text-xs text-foreground-600">Khi khách gửi yêu cầu, chúng sẽ xuất hiện ở đây.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-3 items-start">
          <div className="lg:col-span-2 rounded-xl border border-background-200/60 bg-background-100 overflow-hidden">
            <div className="px-3 py-2 border-b border-background-200/60 bg-background-50">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground-500">Danh sách ({requests.length})</p>
            </div>
            <div className="max-h-[520px] overflow-y-auto divide-y divide-background-200/40">
              {requests.map((r) => (
                <button
                  key={r.id}
                  onClick={() => setSelectedId(r.id)}
                  className={`w-full text-left px-3 py-2.5 transition-colors cursor-pointer ${selectedId === r.id ? 'bg-primary-500/10' : 'hover:bg-background-200/40'}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-[11px] font-bold tracking-widest text-foreground-950">{r.request_code}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${statusMeta[r.status]?.cls || 'bg-foreground-200/40 text-foreground-700'}`}>
                      {statusMeta[r.status]?.label || r.status}
                    </span>
                  </div>
                  <p className="mt-0.5 text-sm font-semibold text-foreground-900 line-clamp-1">{r.subject}</p>
                  <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-foreground-500">
                    <span className="truncate">{r.name}</span>
                    <span>•</span>
                    <span className="whitespace-nowrap">{formatDate(r.created_at)}</span>
                  </div>
                  {r.reply_count > 0 && (
                    <p className="mt-0.5 text-[11px] text-primary-400 font-semibold">{r.reply_count} phản hồi</p>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="lg:col-span-3 rounded-xl border border-background-200/60 bg-background-100 overflow-hidden">
            {!selected ? (
              <div className="p-8 text-center text-sm text-foreground-600">Chọn một yêu cầu để xem chi tiết.</div>
            ) : (
              <div className="flex flex-col">
                <div className="px-4 py-3 border-b border-background-200/60">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <h2 className="text-sm font-bold text-foreground-950 line-clamp-2">{selected.subject}</h2>
                      <p className="text-[11px] text-foreground-500 mt-0.5">
                        <span className="font-semibold text-foreground-700">{selected.name}</span> • {formatDate(selected.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <select
                        value={selected.status}
                        onChange={(e) => handleStatusChange(e.target.value)}
                        className={`px-2 py-1 rounded-full text-[10px] font-semibold border-none outline-none cursor-pointer ${statusMeta[selected.status]?.cls || 'bg-foreground-200/40 text-foreground-700'}`}
                      >
                        <option value="open">Mới</option>
                        <option value="answered">Đã trả lời</option>
                        <option value="resolved">Đã giải quyết</option>
                      </select>
                      <span className="font-mono text-[11px] font-bold tracking-widest text-foreground-950">{selected.request_code}</span>
                    </div>
                  </div>
                </div>

                <div className="px-4 py-3 border-b border-background-200/60 bg-background-50">
                  <p className="text-sm text-foreground-800 whitespace-pre-wrap">{selected.message}</p>
                </div>

                <div className="flex-1 px-4 py-3 space-y-2 overflow-y-auto max-h-[240px]">
                  {replyLoading ? (
                    <p className="text-xs text-foreground-500">Đang tải phản hồi...</p>
                  ) : replies.length === 0 ? (
                    <p className="text-xs text-foreground-600">Chưa có phản hồi.</p>
                  ) : (
                    replies.map((r, i) => (
                      <div key={i} className={`rounded-lg p-2.5 border ${r.is_staff ? 'bg-primary-500/10 border-primary-500/20' : 'bg-background-50 border-background-200/60'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${r.is_staff ? 'bg-primary-500/20 text-primary-400' : 'bg-foreground-200/40 text-foreground-700'}`}>
                            {r.is_staff ? 'Hỗ trợ viên' : 'Khách'}
                          </span>
                          <span className="text-[10px] text-foreground-500">{formatDate(r.created_at)}</span>
                        </div>
                        <p className="text-sm text-foreground-800 whitespace-pre-wrap">{r.content}</p>
                      </div>
                    ))
                  )}
                </div>

                <form onSubmit={handleReply} className="px-4 py-3 border-t border-background-200/60">
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    maxLength={2000}
                    rows={2}
                    placeholder="Viết câu trả lời cho khách..."
                    className="w-full px-3 py-2 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500 resize-none"
                  />
                  <div className="mt-2 flex items-center justify-between gap-3">
                    <p className="text-xs text-foreground-500">{replyText.length}/2000</p>
                    <button
                      type="submit"
                      disabled={sending || !replyText.trim()}
                      className="px-4 py-2 rounded-md bg-primary-500 text-background-950 font-semibold text-xs hover:bg-primary-600 disabled:opacity-60 whitespace-nowrap cursor-pointer transition-colors"
                    >
                      {sending ? 'Đang gửi...' : 'Gửi trả lời'}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}